/* @flow */
/**
 * Manages external link and dashboard data retrieval for the Redux store.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';

const name = 'externalLink';

function createInitialState() {
  return {
    usefulLinks: {}
  };
}

/**
 * Reducer that stores useful-link data returned by the external link API.
 */
function updateUsefulLinkData(state, action) {
  return {
    ...state,
    usefulLinks: action.payload
  };
}

/**
 * Reducer that returns the current external-link state when a reset action is dispatched.
 */
function resetToInitialState(state) {
  return {
    ...state
  };
}

/**
 * Builds the reducer map consumed by the external link slice.
 */
function createReducers() {
  return {
    updateUsefulLinkData,
    resetToInitialState
  };
}

const initialState = createInitialState();
const reducers = createReducers();
const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Async thunk that calls the EXTERNALLINK API and updates usefulLinks in state.
 */
function fetchUsefulLink() {
  return createAsyncThunk(
    `${name}/fetchUsefulLink`,
    async (_, { dispatch }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'EXTERNALLINK',
        method: 'get'
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        dispatch(actions.updateUsefulLinkData(response.data?.usefulLinks));
      }).catch(() => {
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Async thunk that calls the DASHBOARD API and returns the response through a callback.
 */
function fetchDashboard() {
  return createAsyncThunk(
    `${name}/fetchDashboard`,
    async ({ callback }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'DASHBOARD',
        method: 'get'
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(response);
        }
      }).catch(() => {
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Async thunk that resets external-link slice data when navigation leaves the external link page.
 */
function resetExternalLink() {
  return createAsyncThunk(
    `${name}/resetExternalLink`,
    (currentPage, { getState, dispatch }) => {
      const state = getState();
      // Accept either a route key string or a navigation payload object.
      const page = typeof currentPage === 'string' ? currentPage : currentPage?.currentPage;
      // Keep external-link state only while the external link page is active.
      if (state.externalLink?.usefulLinks && page !== 'EXTERNALINK') {
        dispatch(actions.resetToInitialState());
      }
    }
  );
}

/**
 * Builds thunk action creators exported with the external link slice actions.
 */
function createExtraActions() {
  return {
    resetExternalLink: resetExternalLink(),
    fetchUsefulLink: fetchUsefulLink(),
    fetchDashboard: fetchDashboard()
  };
}

const extraActions = createExtraActions();

export const externalLinkActions = { ...actions, ...extraActions };
export const externalLinkReducer = slice.reducer;
